/**
 * User notifications API functions
 *
 * Typed wrappers + DTO re-exports for the `/user-notifications/*` surface.
 * Schemas come from `types.generated.ts` (OpenAPI spec → TS codegen, xm0.5.1).
 * Endpoint surface set in #yuklar-xm0.4.12 (Path A additions).
 */

import { apiClient } from "./client";
import type { PaginatedResponse } from "./types";
import type { components } from "./types.generated";

type Schemas = components["schemas"];

// Notification DTOs re-exported from generated OpenAPI schemas (yuklar-xm0.5.7).
// Source of truth: api/src/router/user-notifications Path A.
export type Notification = Schemas["Notification"];
export type NotificationReference = Schemas["NotificationReference"];
export type NotificationPreferences = Schemas["NotificationPreferences"];
export type NotificationSearchHistoryItem =
  Schemas["NotificationSearchHistoryItem"];
export type UpdateNotificationPreferencesRequest =
  Schemas["UpdateNotificationPreferencesRequest"];
export type MarkContextReadRequest = Schemas["MarkContextReadRequest"];

/**
 * Get paginated notifications for the current user.
 */
export const getNotifications = async (params: {
  language: string;
  query?: string;
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<Notification>> => {
  const search = new URLSearchParams();
  search.set("language", params.language);
  if (params.query !== undefined) search.set("query", params.query);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  return apiClient.get<PaginatedResponse<Notification>>(
    `/user-notifications?${search.toString()}`,
  );
};

/**
 * Get a single notification by id.
 */
export const getNotification = async (
  id: string,
  language: string,
): Promise<Notification> => {
  return apiClient.get<Notification>(
    `/user-notifications/${encodeURIComponent(id)}?language=${encodeURIComponent(language)}`,
  );
};

/**
 * Get the current user's per-category notification channel preferences.
 */
export const getNotificationPreferences =
  async (): Promise<NotificationPreferences> => {
    return apiClient.get<NotificationPreferences>(
      "/user-notifications/preferences",
    );
  };

/**
 * Update the current user's per-category notification channel preferences.
 */
export const updateNotificationPreferences = async (
  body: UpdateNotificationPreferencesRequest,
): Promise<NotificationPreferences> => {
  return apiClient.patch<NotificationPreferences>(
    "/user-notifications/preferences",
    body,
  );
};

/**
 * Mark all notifications as read for the current user. Returns the ids that were marked.
 */
export const markAllNotificationRead = async (
  language: string,
): Promise<string[]> => {
  return apiClient.post<string[]>(
    `/user-notifications/mark-read-all?language=${encodeURIComponent(language)}`,
  );
};

/**
 * Mark every notification whose `thread_key` matches `thread_key_prefix` as read.
 * Returns the count of notifications marked.
 */
export const markContextRead = async (
  body: MarkContextReadRequest,
): Promise<{ marked: number }> => {
  return apiClient.post<{ marked: number }>(
    "/user-notifications/mark-context-read",
    body,
  );
};

/**
 * Mark a single notification as read. Returns the updated notification.
 */
export const markNotificationRead = async (
  id: string,
  language: string,
): Promise<Notification> => {
  return apiClient.post<Notification>(
    `/user-notifications/mark-read/${encodeURIComponent(id)}?language=${encodeURIComponent(language)}`,
  );
};

/**
 * Get the current user's notification search history (paginated).
 */
export const getNotificationSearchHistory = async (params?: {
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<NotificationSearchHistoryItem>> => {
  const search = new URLSearchParams();
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.offset !== undefined) search.set("offset", String(params.offset));
  const query = search.toString();
  return apiClient.get<PaginatedResponse<NotificationSearchHistoryItem>>(
    query
      ? `/user-notifications/search-history?${query}`
      : "/user-notifications/search-history",
  );
};

/**
 * Soft-delete a single search-history entry. Returns the (now `is_deleted: true`) item.
 */
export const deleteNotificationSearchHistory = async (
  id: string,
): Promise<NotificationSearchHistoryItem> => {
  return apiClient.delete<NotificationSearchHistoryItem>(
    `/user-notifications/search-history/${encodeURIComponent(id)}`,
  );
};
